import React from 'react';
import { Link } from 'react-router-dom';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-xs">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/" className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Cookie Policy</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <div className="prose prose-slate max-w-none">
            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">1. What Are Cookies?</h2>
            <p className="text-slate-700 mb-4">
              Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences and improve your experience.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">2. How We Use Cookies</h2>
            <p className="text-slate-700 mb-4">
              MyInvestFolio &mdash; across both Continuity System and Portfolio Clarity &mdash; uses a minimal, security-focused approach to cookies. Your authentication token is stored locally on your device (not as a browser cookie), with a small session cookie kept only as a fallback.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">3. Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.1 Essential Cookies (Required)</h3>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <p className="text-slate-700 mb-2">
                <strong>Purpose:</strong> Authentication and security
              </p>
              <p className="text-slate-700 mb-2">
                <strong>Cookies Used:</strong>
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-1">
                <li><code className="bg-slate-100 px-2 py-1 rounded">session_token</code> - User authentication (fallback)</li>
                <li><code className="bg-slate-100 px-2 py-1 rounded">admin_session_token</code> - Admin authentication (fallback)</li>
              </ul>
              <p className="text-slate-700 mt-2">
                <strong>Duration:</strong> 7 days
              </p>
              <p className="text-slate-700 mt-2">
                <strong>Can be disabled?</strong> No - Required for the service to function
              </p>
            </div>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.2 JWT Tokens (Primary Authentication)</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-slate-700 mb-2">
                <strong>Storage Method:</strong> localStorage (not cookies)
              </p>
              <p className="text-slate-700 mb-2">
                <strong>Token Name:</strong> <code className="bg-slate-100 px-2 py-1 rounded">auth_token</code>
              </p>
              <p className="text-slate-700 mb-2">
                <strong>Purpose:</strong> Primary authentication mechanism
              </p>
              <p className="text-slate-700 mt-2">
                <strong>Duration:</strong> 7 days
              </p>
            </div>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.3 Optional Analytics &amp; Monitoring (Consent-Gated)</h3>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-slate-700 mb-2">
                <strong>Purpose:</strong> Product analytics and error monitoring
              </p>
              <p className="text-slate-700 mb-2">
                <strong>Status:</strong> <span className="font-semibold text-amber-700">Disabled by default.</span> These services only load if you explicitly opt in via the Cookie Consent Banner or the Cookie Preferences panel in <Link to="/settings" className="text-emerald-600 hover:underline">Settings</Link>.
              </p>
              <p className="text-slate-700 mb-2">
                <strong>Services:</strong> PostHog (product analytics &amp; session replay) and Sentry (error monitoring). See Section 5 for full details.
              </p>
              <p className="text-slate-700 mt-2">
                <strong>Can be disabled?</strong> Yes — at any time, with no impact on core functionality.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.4 What We DON'T Use</h3>
            <p className="text-slate-700 mb-2">
              MyInvestFolio does NOT use:
            </p>
            <ul className="list-disc pl-6 text-slate-700 mb-4 space-y-2">
              <li>❌ Advertising or retargeting cookies</li>
              <li>❌ Google Analytics or similar third-party ad-tech trackers</li>
              <li>❌ Social media cookies</li>
              <li>❌ Marketing or behavioural-profiling cookies</li>
              <li>❌ Any tracker that loads before you give consent</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">4. Cookie Management</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.1 Browser Settings</h3>
            <p className="text-slate-700 mb-4">
              You can control cookies through your browser settings:
            </p>
            <ul className="list-disc pl-6 text-slate-700 mb-4 space-y-2">
              <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
              <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
              <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.2 Impact of Blocking Cookies</h3>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
              <p className="text-slate-700">
                <strong>⚠️ Warning:</strong> Blocking essential cookies will prevent you from logging in and using MyInvestFolio. Our authentication system requires either JWT tokens (localStorage) or session cookies to function.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">5. Third-Party Services</h2>
            <p className="text-slate-700 mb-4">
              We use the following third-party services. Analytics and monitoring services are <strong>strictly consent-gated</strong> &mdash; they do not load or send any data until you opt in.
            </p>
            
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="font-semibold text-slate-900 mb-2">Stripe (Payment Processing)</p>
                <p className="text-slate-700 text-sm mb-2">
                  Used for secure payment processing. Stripe may set cookies to prevent fraud and ensure secure transactions.
                </p>
                <p className="text-slate-700 text-sm">
                  <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                    Stripe Privacy Policy →
                  </a>
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="font-semibold text-slate-900 mb-2">Resend (Email Services)</p>
                <p className="text-slate-700 text-sm mb-2">
                  Used for sending transactional emails (account verification, password resets, billing receipts). No cookies are set on your browser.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="font-semibold text-slate-900 mb-2">
                  PostHog (Product Analytics &amp; Session Replay) <span className="ml-2 text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Consent required</span>
                </p>
                <p className="text-slate-700 text-sm mb-2">
                  <strong>What it collects (only if you opt in):</strong> anonymised page views, button clicks, navigation paths, device/browser type, approximate region, and optional session replays (mouse movements, clicks, scrolls). Sensitive form fields, passwords, and financial values are masked by default.
                </p>
                <p className="text-slate-700 text-sm mb-2">
                  <strong>Why we'd like to use it:</strong> to understand which features our users use, identify confusing flows, and improve the product.
                </p>
                <p className="text-slate-700 text-sm mb-2">
                  <strong>Default state:</strong> <span className="font-semibold">Off.</span> The PostHog script is not injected into the page until you grant consent.
                </p>
                <p className="text-slate-700 text-sm mb-2">
                  <strong>How to opt out / withdraw consent:</strong> Click "Reject" in the Cookie Consent Banner, or open <Link to="/settings" className="text-emerald-600 hover:underline">Settings → Cookie Preferences</Link> at any time and toggle Analytics off. Withdrawing consent immediately stops further data collection and clears the PostHog session.
                </p>
                <p className="text-slate-700 text-sm">
                  <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                    PostHog Privacy Policy →
                  </a>
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="font-semibold text-slate-900 mb-2">
                  Sentry (Error Monitoring) <span className="ml-2 text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Consent required</span>
                </p>
                <p className="text-slate-700 text-sm mb-2">
                  <strong>What it collects (only if you opt in):</strong> JavaScript error stack traces, the URL where the error occurred, browser/OS version, and a breadcrumb trail of recent UI actions. Personally identifiable information and request bodies are scrubbed before transmission.
                </p>
                <p className="text-slate-700 text-sm mb-2">
                  <strong>Why we'd like to use it:</strong> to detect and fix crashes, broken pages, and performance regressions before they affect more users.
                </p>
                <p className="text-slate-700 text-sm mb-2">
                  <strong>Default state:</strong> <span className="font-semibold">Off.</span> The Sentry SDK is not initialised until you grant consent.
                </p>
                <p className="text-slate-700 text-sm mb-2">
                  <strong>How to opt out / withdraw consent:</strong> Use the Cookie Consent Banner or <Link to="/settings" className="text-emerald-600 hover:underline">Settings → Cookie Preferences</Link> and toggle Error Monitoring off. Existing buffered events are discarded.
                </p>
                <p className="text-slate-700 text-sm">
                  <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                    Sentry Privacy Policy →
                  </a>
                </p>
              </div>
            </div>

            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mt-6 mb-4">
              <p className="text-slate-700 text-sm">
                <strong>Manage your preferences anytime:</strong> visit <Link to="/settings" className="text-emerald-700 hover:underline font-semibold">Settings → Cookie Preferences</Link>. Your choices are stored locally on your device and are honoured on every visit.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">6. Data Retention</h2>
            <p className="text-slate-700 mb-4">
              Session cookies are automatically deleted when you log out or after 7 days of inactivity. JWT tokens in localStorage remain until you explicitly log out or they expire (7 days).
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">7. Updates to This Policy</h2>
            <p className="text-slate-700 mb-4">
              We may update this Cookie Policy to reflect changes in our practices or legal requirements. The "Last updated" date at the top indicates when changes were made.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">8. Contact Us</h2>
            <p className="text-slate-700 mb-4">
              If you have questions about our use of cookies, contact us at:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-slate-700"><strong>Email:</strong> privacy@myinvestfolio.io</p>
              <p className="text-slate-700"><strong>Primary Office:</strong> Nigeria</p>
              <p className="text-slate-700"><strong>Note:</strong> We serve users globally across multiple countries</p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">9. Related Policies</h2>
            <div className="flex flex-wrap gap-4">
              <Link to="/privacy-policy" className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition">
                Privacy Policy →
              </Link>
              <Link to="/terms-of-service" className="inline-flex items-center px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition">
                Terms of Service →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
